import debounce from 'lodash.debounce'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { EntityPage, Filter, Paging } from 'types/paging'

import { useAppErrorContext } from 'contexts/AppErrorContext'
import { AxiosResponse } from 'axios'

interface Params<T> {
  getData: (params: Paging) => Promise<AxiosResponse<EntityPage<T>, any>>
  deleteData?: (id: number) => any
  defaultFilters?: Filter[]
  formValueSetter?: (field: any, value: any) => void
}

export const usePagination = <T>({ getData, deleteData, defaultFilters, formValueSetter }: Params<T>) => {
  const { setAppError } = useAppErrorContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [fetching, setFetching] = useState<boolean>(false)

  const [paging, setPaging] = useState<Paging>({
    page: {
      page_number: 0,
      size: 20,
    },
    order_by: 'created_at',
    direction: 'desc',
    filters: [],
    quick_search: null,
  })

  const [totalElements, setTotalElements] = useState<number>(0)
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(false)

  const [update, setForceUpdate] = useState<boolean>(false)
  const forceUpdate = () => setForceUpdate((prev) => !prev)

  useEffect(() => {
    setLoading(true)
    getData(paging)
      .then((res) => {
        if (paging.page.page_number === 0) {
          setData(res.data.data)
        } else {
          setData((prev) => [...prev, ...res.data.data])
        }
        setTotalElements(res.data.page.total_elements)
        setLoading(false)
      })
      .catch((e: any) => {
        setAppError(e)
        setLoading(false)
      })
  }, [JSON.stringify(paging), update])

  useEffect(() => {
    if (fetching && data.length < (totalElements || 0)) {
      setPaging((prev) => ({ ...prev, page: { ...prev.page, page_number: (prev.page.page_number || 0) + 1 } }))
      setFetching(false)
    }
  }, [fetching, data.length])

  const scrollHandler = (e: any) => {
    if (e.target.scrollHeight - (e.target.scrollTop + window.innerHeight) < 50) {
      setFetching(true)
    }
  }

  useEffect(() => {
    document?.getElementById('idForPagination')?.addEventListener('scroll', scrollHandler)
    document?.getElementById('idForPagination')?.scrollTo({ top: 0 })
    return () => document?.getElementById('idForPagination')?.removeEventListener('scroll', scrollHandler)
  }, [])

  const onSearchParamsChange = useCallback(
    debounce((searchParams) => {
      const objParams = Object.fromEntries(searchParams)
      const filtersArray: Filter[] = []
      let search: string | null = null
      let orderAndDirection: string[] = []
      let direction: string | null = 'desc'
      let orderBy: string | null = 'created_at'

      for (const key in objParams) {
        if (key.includes('f__')) {
          const column = key.split('f__')[1]
          const valueTypeOperator = objParams[key].split('-')
          filtersArray.push({
            column: column,
            value_type: valueTypeOperator[1],
            operator: valueTypeOperator[2] === 'eq' ? '=' : '!=',
            values: [...valueTypeOperator[0].split(',')],
          })
        }
        if (key.includes('s__')) {
          search = objParams[key]
        }
        if (key.includes('d__')) {
          orderAndDirection = objParams[key].split('-')
          direction = orderAndDirection[1]
          orderBy = orderAndDirection[0]
        }
      }
      setPaging((prev) => ({
        ...prev,
        filters: defaultFilters ? [...defaultFilters, ...filtersArray] : [...filtersArray],
        direction,
        order_by: orderBy,
        quick_search: search,
        page: { ...prev.page, page_number: 0 },
      }))
    }, 400),
    []
  )

  useEffect(() => {
    setLoading(true)
    onSearchParamsChange(searchParams)
  }, [JSON.stringify(Object.fromEntries(searchParams))])

  const handleFilterChange = ({ fieldName, searchParamName, option }: HandleFilterChangeParams) => {
    if (formValueSetter) formValueSetter(`${fieldName}`, option)
    if (option.value.length) {
      setSearchParams({ ...Object.fromEntries(searchParams), [`${searchParamName}`]: option.value })
    } else {
      searchParams.delete(searchParamName)
      setSearchParams(searchParams)
    }
  }

  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleDelete = async (id: number) => {
    setDeleteLoading(true)
    try {
      if (deleteData) await deleteData(id)
      else throw new Error('No delete API instance provided.')
    } catch (error: any) {
      console.error(error)
      setAppError(error)
    } finally {
      setDeleteLoading(false)
      setPaging((prev) => ({ ...prev, page: { ...prev.page, page_number: 0 } }))
      forceUpdate()
    }
  }

  return {
    data,
    loading,
    totalElements,
    paging,
    setPaging,
    forceUpdate,
    handleFilterChange,
    handleDelete,
    deleteLoading,
  }
}

interface HandleFilterChangeParams {
  fieldName: string
  searchParamName: string
  option: any
}
